import React from 'react';
import { useTranslation } from 'react-i18next';
import { Share, ChevronLeft, ChevronRight, Book, Copy, Plus, Heart, Star, Link2, Bookmark } from 'lucide-react';

/**
 * IosSafariScreenshots — Phase 47 · Feb 2026
 * ------------------------------------------------------------------
 * Three lightweight, high-fidelity CSS/SVG illustrations that mimic
 * real iOS 17 Safari UI for the "how to install as PWA" tutorial.
 *
 * These are intentionally NOT actual Apple screenshots (we cannot
 * ship copyrighted assets) but a didactic reconstruction. The prop
 * `highlighted` drives the golden focus ring so the user's eye is
 * pulled straight to the tapped element.
 *
 * When real iPhone screenshots are provided by the marketing team,
 * the parent component can swap these three renders with plain
 * `<img src="/pwa-tutorial/step-N.png"/>` tags without touching the
 * modal layout. Every illustration keeps the same 3:5 iPhone-frame
 * aspect ratio to guarantee visual continuity.
 */

const Frame = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="relative mx-auto aspect-[9/16] w-full max-w-[190px] overflow-hidden rounded-[26px] border-[3px] border-neutral-800 bg-[#0a0a0a] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)]"
  >
    {/* subtle screen glare */}
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.03] via-transparent to-transparent" />
    {children}
  </div>
);

/**
 * Step 1 — iOS Safari bottom toolbar. Share button glows.
 */
export const SafariBottomBarShot = () => (
  <Frame testId="ios-shot-step1">
    {/* Top status bar (mock) */}
    <div className="flex items-center justify-between px-3 pt-2 text-[8px] text-white/60">
      <span className="font-semibold">9:41</span>
      <span className="flex items-center gap-0.5">
        <span className="h-1 w-1 rounded-full bg-white/70" />
        <span className="h-1 w-1 rounded-full bg-white/70" />
        <span className="h-1 w-1 rounded-full bg-white/70" />
      </span>
    </div>
    {/* Mock page — Nymia logotype */}
    <div className="flex h-[75%] flex-col items-center justify-center gap-2 px-4">
      <div className="text-4xl font-serif text-amber-400/95">N</div>
      <div className="text-[9px] font-medium tracking-[0.14em] text-white/80">Nymia</div>
      <div className="mt-1 text-[6.5px] uppercase tracking-[0.22em] text-white/40">Voice-First</div>
    </div>
    {/* Safari bottom bar */}
    <div className="absolute inset-x-0 bottom-0 h-[22%] border-t border-white/5 bg-[#1c1c1e]/95 backdrop-blur">
      <div className="flex h-full items-center justify-around px-1 pb-1">
        <ChevronLeft className="h-3.5 w-3.5 text-white/70" />
        <ChevronRight className="h-3.5 w-3.5 text-white/30" />
        {/* Share — highlighted with gold ring + pulse */}
        <div className="relative">
          <span className="absolute -inset-[6px] animate-ping rounded-full border border-amber-400/70" aria-hidden="true" />
          <span className="absolute -inset-[4px] rounded-full border-2 border-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.7)]" aria-hidden="true" />
          <Share className="relative h-4 w-4 text-amber-300" strokeWidth={2.2} />
        </div>
        <Book className="h-3.5 w-3.5 text-white/70" />
        <Copy className="h-3.5 w-3.5 text-white/70" />
      </div>
      {/* iOS home indicator */}
      <div className="mx-auto -mt-0.5 h-[3px] w-8 rounded-full bg-white/50" />
    </div>
  </Frame>
);

/**
 * Step 2 — iOS Share sheet. "Add to Home Screen" row glows.
 */
export const SafariShareMenuShot = () => {
  const { t } = useTranslation('pwa');
  return (
    <Frame testId="ios-shot-step2">
      {/* Status bar */}
      <div className="flex items-center justify-between px-3 pt-2 text-[8px] text-white/60">
        <span className="font-semibold">9:41</span>
        <span className="text-[7px]">nymia.me</span>
      </div>
      {/* Dimmed page background */}
      <div className="absolute inset-0 bg-black/70" />
      {/* Share sheet */}
      <div className="absolute inset-x-1 bottom-1 rounded-2xl bg-[#1c1c1e]/98 p-2 shadow-lg backdrop-blur">
        {/* Sheet header — page preview */}
        <div className="mb-2 flex items-center gap-2 rounded-lg bg-white/[0.04] p-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-black">
            <span className="font-serif text-[11px] text-amber-400">N</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[8.5px] font-semibold text-white">nymia.me</div>
            <div className="truncate text-[6.5px] text-white/40">Voice-First</div>
          </div>
        </div>
        {/* Sheet action list */}
        <div className="space-y-[3px]">
          <ShareRow icon={<Heart className="h-2.5 w-2.5" />} label={t('tutorial.step2.rows.favorites')} />
          <ShareRow icon={<Star className="h-2.5 w-2.5" />} label={t('tutorial.step2.rows.bookmarks')} />
          {/* Highlighted */}
          <div
            className="relative flex items-center gap-2 rounded-md border-2 border-amber-400 bg-amber-400/10 px-2 py-1.5 shadow-[0_0_10px_rgba(251,191,36,0.35)]"
            data-testid="ios-shot-step2-target"
          >
            <span className="absolute -inset-[3px] animate-ping rounded-md border border-amber-400/60" aria-hidden="true" />
            <div className="flex h-4 w-4 items-center justify-center rounded-sm bg-amber-400 text-black">
              <Plus className="h-2.5 w-2.5" strokeWidth={3} />
            </div>
            <span className="text-[8px] font-semibold text-amber-100">{t('tutorial.step2.rows.add_home')}</span>
          </div>
          <ShareRow icon={<Copy className="h-2.5 w-2.5" />} label={t('tutorial.step2.rows.copy')} />
          <ShareRow icon={<Link2 className="h-2.5 w-2.5" />} label={t('tutorial.step2.rows.reading_list')} />
          <ShareRow icon={<Bookmark className="h-2.5 w-2.5" />} label={t('tutorial.step2.rows.bookmark')} />
        </div>
      </div>
      {/* iOS home indicator */}
      <div className="absolute inset-x-0 bottom-0 flex justify-center pb-0.5">
        <div className="h-[3px] w-8 rounded-full bg-white/60" />
      </div>
    </Frame>
  );
};

const ShareRow = ({ icon, label }) => (
  <div className="flex items-center gap-2 rounded-md px-2 py-1.5 text-white/70">
    <div className="flex h-4 w-4 items-center justify-center rounded-sm bg-white/10">
      {icon}
    </div>
    <span className="text-[7.5px] font-medium">{label}</span>
  </div>
);

const DockIcon = ({ children, bg }) => (
  <div className={`flex h-6 w-6 items-center justify-center rounded-[7px] ${bg} shadow`}>
    {children}
  </div>
);

/**
 * Step 3 — iOS "Add to Home Screen" confirmation sheet. "Add" glows.
 */
export const AddConfirmShot = () => {
  const { t } = useTranslation('pwa');
  return (
    <Frame testId="ios-shot-add-confirm">
      {/* Status bar */}
      <div className="flex items-center justify-between px-3 pt-2 text-[8px] text-white/60">
        <span className="font-semibold">9:41</span>
        <span className="text-[7px]">nymia.me</span>
      </div>
      <div className="absolute inset-0 bg-black/70" />
      {/* Confirmation sheet */}
      <div className="absolute inset-x-1 top-[16%] rounded-2xl bg-[#1c1c1e]/95 p-2.5 shadow-lg backdrop-blur">
        <div className="flex items-center justify-between gap-1">
          <span className="text-[7.5px] text-white/50">{t('tutorial.add_sheet.cancel')}</span>
          <span className="max-w-[76px] text-center text-[6.5px] font-semibold leading-tight text-white/90">
            {t('tutorial.step2.rows.add_home')}
          </span>
          {/* Add — highlighted with gold ring + pulse */}
          <div className="relative" data-testid="ios-shot-add-confirm-target">
            <span className="absolute -inset-[4px] animate-ping rounded-md border border-amber-400/70" aria-hidden="true" />
            <span className="relative inline-block rounded-md border-2 border-amber-400 bg-amber-400/15 px-1.5 py-0.5 text-[8px] font-bold text-amber-300 shadow-[0_0_10px_rgba(251,191,36,0.5)]">
              {t('tutorial.add_sheet.add')}
            </span>
          </div>
        </div>
        <div className="mt-2.5 flex items-center gap-2 rounded-lg bg-white/[0.06] p-2">
          <img src="/pwa/apple-touch-icon.png" alt="Nymia icon" className="h-7 w-7 rounded-[7px]" />
          <div className="min-w-0 flex-1 border-b border-white/15 pb-1">
            <div className="text-[9px] font-semibold text-white">Nymia</div>
          </div>
        </div>
        <div className="mt-1.5 truncate rounded-lg bg-white/[0.04] p-2 text-[7px] text-white/40">https://nymia.me</div>
      </div>
      {/* Fake keyboard */}
      <div className="absolute inset-x-0 bottom-0 space-y-1 bg-[#2b2b2d] p-1.5 pb-2.5">
        {[10, 9, 7].map((n, r) => (
          <div key={r} className="flex justify-center gap-[3px]">
            {Array.from({ length: n }).map((_, i) => (
              <span key={i} className="h-4 w-[8%] rounded-[3px] bg-white/20" />
            ))}
          </div>
        ))}
        <div className="flex justify-center">
          <span className="h-4 w-[55%] rounded-[3px] bg-white/20" />
        </div>
      </div>
    </Frame>
  );
};

/**
 * Step 4 — iPhone Home Screen with Nymia installed.
 * We use the real /pwa/apple-touch-icon.png so the user sees the
 * actual icon they'll get.
 */
export const HomeScreenShot = () => {
  return (
    <Frame testId="ios-shot-step3">
      {/* Status bar */}
      <div className="flex items-center justify-between px-3 pt-2 text-[8px] text-white/80">
        <span className="font-semibold">9:41</span>
        <span className="text-[7px]">•••</span>
      </div>
      {/* Wallpaper gradient */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#3a1a55] via-[#1a1a2e] to-[#0f0517]" />
      {/* App grid */}
      <div className="mt-3 grid grid-cols-4 gap-2 px-2">
        {/* Nymia — highlighted */}
        <div
          className="flex flex-col items-center"
          data-testid="ios-shot-step3-target"
        >
          <div className="relative">
            <span className="absolute -inset-[3px] animate-ping rounded-[8px] border border-amber-400/70" aria-hidden="true" />
            <img
              src="/pwa/apple-touch-icon.png"
              alt="Nymia icon"
              className="relative h-7 w-7 rounded-[8px] border border-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.55)]"
            />
          </div>
          <span className="mt-0.5 text-[6px] font-medium text-white">Nymia</span>
        </div>
        {/* Filler app tiles */}
        {[
          'bg-blue-500', 'bg-emerald-500', 'bg-rose-500',
          'bg-orange-500', 'bg-violet-500', 'bg-yellow-500', 'bg-teal-500',
          'bg-pink-500', 'bg-slate-400', 'bg-lime-500', 'bg-indigo-500',
        ].map((c, i) => (
          <div key={i} className="flex flex-col items-center">
            <div className={`h-7 w-7 rounded-[8px] ${c} opacity-70 shadow`} />
            <span className="mt-0.5 h-1 w-4 rounded bg-white/25" />
          </div>
        ))}
      </div>
      {/* Page dots */}
      <div className="absolute inset-x-0 bottom-9 flex justify-center gap-1">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`h-1 w-1 rounded-full ${i === 0 ? 'bg-white' : 'bg-white/40'}`} />
        ))}
      </div>
      {/* Dock */}
      <div className="absolute inset-x-2 bottom-2 flex items-center justify-around rounded-2xl bg-white/10 p-1.5 backdrop-blur-sm">
        <DockIcon bg="bg-green-500">
          <span className="text-[8px]">☎</span>
        </DockIcon>
        <DockIcon bg="bg-white">
          <span className="text-[8px] text-blue-500">🧭</span>
        </DockIcon>
        <DockIcon bg="bg-green-400">
          <span className="text-[8px]">💬</span>
        </DockIcon>
        <DockIcon bg="bg-red-500">
          <span className="text-[8px]">♪</span>
        </DockIcon>
      </div>
      {/* iOS home indicator */}
      <div className="absolute inset-x-0 bottom-0 flex justify-center pb-0.5">
        <div className="h-[3px] w-8 rounded-full bg-white/70" />
      </div>
    </Frame>
  );
};
